import React, { useState, useEffect } from 'react';
import './SKUList.css';
import API_BASE_URL from '../config';

const SKUList = () => {
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [duplicatingSku, setDuplicatingSku] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Form state for adding/editing
  const [formData, setFormData] = useState({
    IGD_SKU_number: '',
    IGD_Name: '',
    IGD_Description: '',
    Shopify_SKU_Variant: '',
    Amazon_SKU_Name: '',
    ASIN: '',
    Category: '',
    Shopify_Bundle: '',
    Amazon_Bundle: '',
    variant: ''
  });

  // Fetch SKUs from the backend
  const fetchSKUs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/skus`);
      if (!response.ok) {
        throw new Error('Failed to fetch SKUs');
      }
      const data = await response.json();
      setSkus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSKUs();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      IGD_SKU_number: '',
      IGD_Name: '',
      IGD_Description: '',
      Shopify_SKU_Variant: '',
      Amazon_SKU_Name: '',
      ASIN: '',
      Category: '',
      Shopify_Bundle: '',
      Amazon_Bundle: '',
      variant: ''
    });
    setEditingSku(null);
    setDuplicatingSku(null);
    setShowAddForm(false);
  };

  // Add new SKU
  const handleAddSKU = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/skus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 400 && errorData.error === 'SKU already exists') {
          throw new Error('SKU already exists. Use the edit button to modify existing SKUs.');
        }
        throw new Error(errorData.error || 'Failed to add SKU');
      }

      await fetchSKUs();
      resetForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Update existing SKU
  const handleUpdateSKU = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/skus/${editingSku.IGD_SKU_number}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update SKU');
      }

      await fetchSKUs();
      resetForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete SKU
  const handleDeleteSKU = async (skuNumber) => {
    if (!window.confirm(`Are you sure you want to delete SKU ${skuNumber}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/skus/${skuNumber}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete SKU');
      }

      await fetchSKUs();
    } catch (err) {
      setError(err.message);
    }
  };

  // Start editing SKU
  const handleEditSKU = (sku) => {
    setFormData({
      IGD_SKU_number: sku.IGD_SKU_number,
      IGD_Name: sku.IGD_Name || '',
      IGD_Description: sku.IGD_Description || '',
      Shopify_SKU_Variant: sku.Shopify_SKU_Variant || '',
      Amazon_SKU_Name: sku.Amazon_SKU_Name || '',
      ASIN: sku.ASIN || '',
      Category: sku.Category || '',
      Shopify_Bundle: sku.Shopify_Bundle || '',
      Amazon_Bundle: sku.Amazon_Bundle || '',
      variant: sku.variant || ''
    });
    setEditingSku(sku);
    setDuplicatingSku(null);
    setShowAddForm(true);
  };

  // Start duplicating SKU
  const handleDuplicateSKU = (sku) => {
    setFormData({
      IGD_SKU_number: '', // Clear SKU number for new entry
      IGD_Name: sku.IGD_Name || '',
      IGD_Description: sku.IGD_Description || '',
      Shopify_SKU_Variant: sku.Shopify_SKU_Variant || '',
      Amazon_SKU_Name: sku.Amazon_SKU_Name || '',
      ASIN: sku.ASIN || '',
      Category: sku.Category || '',
      Shopify_Bundle: sku.Shopify_Bundle || '',
      Amazon_Bundle: sku.Amazon_Bundle || '',
      variant: sku.variant || ''
    });
    setDuplicatingSku(sku);
    setEditingSku(null);
    setShowAddForm(true);
  };

  // Filter and search SKUs
  const filteredSkus = skus.filter(sku => {
    const matchesSearch = 
      sku.IGD_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sku.IGD_SKU_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sku.Category?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !filterCategory || sku.Category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Get unique categories for filter
  const categories = [...new Set(skus.map(sku => sku.Category).filter(Boolean))];

  // Determine form title and submit button text
  const getFormTitle = () => {
    if (editingSku) return 'Edit SKU';
    if (duplicatingSku) return 'Duplicate SKU';
    return 'Add New SKU';
  };

  const getSubmitButtonText = () => {
    if (editingSku) return 'Update SKU';
    if (duplicatingSku) return 'Create Duplicate SKU';
    return 'Add SKU';
  };

  if (loading) {
    return (
      <div className="sku-list">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading SKU data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sku-list">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>SKU Master List Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Manage SKU inventory and product information</li>
              <li>Add, edit, and duplicate SKU records</li>
              <li>Track product variants across sales channels</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {error && (
        <div className="error-message">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="controls-section">
        <div className="search-filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search SKUs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="category-filter"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <button 
          className="add-button"
          onClick={() => setShowAddForm(true)}
        >
          + Add New SKU
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>{getFormTitle()}</h2>
              <button className="close-button" onClick={resetForm}>×</button>
            </div>
            
            {duplicatingSku && (
              <div className="duplicate-notice">
                <p>📋 Duplicating from: <strong>{duplicatingSku.IGD_SKU_number} - {duplicatingSku.IGD_Name}</strong></p>
                <p>Please enter a new SKU number and modify any fields as needed.</p>
              </div>
            )}
            
            <form onSubmit={editingSku ? handleUpdateSKU : handleAddSKU}>
              <div className="form-grid">
                <div className="form-group">
                  <label>IGD SKU Number *</label>
                  <input
                    type="text"
                    name="IGD_SKU_number"
                    value={formData.IGD_SKU_number}
                    onChange={handleInputChange}
                    required
                    disabled={!!editingSku}
                    placeholder={duplicatingSku ? "Enter new SKU number..." : "Enter SKU number"}
                  />
                </div>

                <div className="form-group">
                  <label>IGD Name</label>
                  <input
                    type="text"
                    name="IGD_Name"
                    value={formData.IGD_Name}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                  />
                </div>

                <div className="form-group full-width">
                  <label>IGD Description</label>
                  <textarea
                    name="IGD_Description"
                    value={formData.IGD_Description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Enter product description"
                  />
                </div>

                <div className="form-group">
                  <label>Shopify SKU Variant</label>
                  <input
                    type="text"
                    name="Shopify_SKU_Variant"
                    value={formData.Shopify_SKU_Variant}
                    onChange={handleInputChange}
                    placeholder="Enter Shopify SKU"
                  />
                </div>

                <div className="form-group">
                  <label>Amazon SKU Name</label>
                  <input
                    type="text"
                    name="Amazon_SKU_Name"
                    value={formData.Amazon_SKU_Name}
                    onChange={handleInputChange}
                    placeholder="Enter Amazon SKU"
                  />
                </div>

                <div className="form-group">
                  <label>ASIN</label>
                  <input
                    type="text"
                    name="ASIN"
                    value={formData.ASIN}
                    onChange={handleInputChange}
                    placeholder="Enter ASIN"
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    name="Category"
                    value={formData.Category}
                    onChange={handleInputChange}
                    placeholder="Enter category"
                  />
                </div>

                <div className="form-group">
                  <label>Variant</label>
                  <input
                    type="text"
                    name="variant"
                    value={formData.variant}
                    onChange={handleInputChange}
                    placeholder="Enter variant"
                  />
                </div>

                <div className="form-group">
                  <label>Shopify Bundle</label>
                  <input
                    type="text"
                    name="Shopify_Bundle"
                    value={formData.Shopify_Bundle}
                    onChange={handleInputChange}
                    placeholder="Enter Shopify bundle"
                  />
                </div>

                <div className="form-group">
                  <label>Amazon Bundle</label>
                  <input
                    type="text"
                    name="Amazon_Bundle"
                    value={formData.Amazon_Bundle}
                    onChange={handleInputChange}
                    placeholder="Enter Amazon bundle"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  {getSubmitButtonText()}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SKU Table */}
      <div className="table-container">
        <table className="sku-table">
          <thead>
            <tr>
              <th>IGD SKU Number</th>
              <th>IGD Name</th>
              <th>Category</th>
              <th>Variant</th>
              <th>ASIN</th>
              <th>Shopify SKU</th>
              <th>Amazon SKU</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSkus.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  {searchTerm || filterCategory ? 'No SKUs match your search criteria' : 'No SKUs found'}
                </td>
              </tr>
            ) : (
              filteredSkus.map(sku => (
                <tr key={sku.IGD_SKU_number}>
                  <td className="sku-number">{sku.IGD_SKU_number}</td>
                  <td className="sku-name">{sku.IGD_Name || '-'}</td>
                  <td className="sku-category">{sku.Category || '-'}</td>
                  <td className="sku-variant">{sku.variant || '-'}</td>
                  <td className="sku-asin">{sku.ASIN || '-'}</td>
                  <td className="sku-shopify">{sku.Shopify_SKU_Variant || '-'}</td>
                  <td className="sku-amazon">{sku.Amazon_SKU_Name || '-'}</td>
                  <td className="actions">
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEditSKU(sku)}
                      title="Edit SKU"
                    >
                      ✏️
                    </button>
                    <button 
                      className="action-btn duplicate"
                      onClick={() => handleDuplicateSKU(sku)}
                      title="Duplicate SKU"
                    >
                      📋
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDeleteSKU(sku.IGD_SKU_number)}
                      title="Delete SKU"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="summary">
        <p>Showing {filteredSkus.length} of {skus.length} SKUs</p>
      </div>
      </div>
    </div>
  );
};

export default SKUList; 